import { createNoise2D, type NoiseFunction2D } from 'simplex-noise';
import { DEG, clamp01 } from '../../core/math';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import type { RenderTarget, TechniqueContext } from '../types';
import type { HeightFieldValues } from './params';

/**
 * The text surface displaced in Z by a noise field, drawn as a wireframe landscape.
 *
 * Only lattice cells whose corners are all inside a glyph are kept, so the mesh has the
 * silhouette of the type rather than being a full sheet that happens to be textured.
 *
 * Projection is done by hand for the same reason as the point cloud: the scene is a few
 * thousand line segments with no lighting, and a hand-rolled projection avoids a second
 * WebGL context entirely.
 */
export interface HeightFieldState {
  /** Lattice positions and heights; -1 in `index` marks a hole. */
  x: Float32Array;
  y: Float32Array;
  z: Float32Array;
  index: Int32Array;
  cols: number;
  rows: number;
  cell: number;
  minZ: number;
  maxZ: number;
  palette: ResolvedPalette;
  built: boolean;
}

function values(ctx: TechniqueContext): HeightFieldValues {
  return ctx.values as unknown as HeightFieldValues;
}

function fbm(noise: NoiseFunction2D, x: number, y: number, octaves: number): number {
  let sum = 0;
  let amp = 1;
  let freq = 1;
  let norm = 0;
  for (let i = 0; i < octaves; i++) {
    sum += noise(x * freq, y * freq) * amp;
    norm += amp;
    amp *= 0.5;
    freq *= 2;
  }
  return norm > 0 ? sum / norm : 0;
}

export function init(ctx: TechniqueContext): HeightFieldState {
  const v = values(ctx);
  const cell = Math.max(2, v.resolution);
  const bounds = ctx.field.bounds;
  const cols = Math.max(2, Math.ceil(bounds.width / cell) + 1);
  const rows = Math.max(2, Math.ceil(bounds.height / cell) + 1);
  const size = cols * rows;

  const noise = createNoise2D(() => ctx.rng.next());
  const state: HeightFieldState = {
    x: new Float32Array(size),
    y: new Float32Array(size),
    z: new Float32Array(size),
    index: new Int32Array(size).fill(-1),
    cols,
    rows,
    cell,
    minZ: 0,
    maxZ: 1,
    palette: resolvePalette(v.palette, ctx.rng.fork(1)),
    built: false,
  };

  const centreX = bounds.x + bounds.width / 2;
  const centreY = bounds.y + bounds.height / 2;
  let minZ = Infinity;
  let maxZ = -Infinity;

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const i = r * cols + c;
      const px = bounds.x + c * cell;
      const py = bounds.y + r * cell;
      if (!ctx.field.isInside(px, py)) continue;

      const h = fbm(noise, px * v.noiseScale, py * v.noiseScale, v.octaves) * v.amplitude + v.depthBase;
      state.x[i] = px - centreX;
      state.y[i] = py - centreY;
      state.z[i] = h;
      state.index[i] = i;
      if (h < minZ) minZ = h;
      if (h > maxZ) maxZ = h;
    }
  }

  state.minZ = minZ === Infinity ? 0 : minZ;
  state.maxZ = maxZ === -Infinity ? 1 : maxZ;
  return state;
}

export function step(state: HeightFieldState, _dt: number, _ctx: TechniqueContext): HeightFieldState {
  state.built = true;
  return state;
}

export function isComplete(state: HeightFieldState): boolean {
  return state.built;
}

export function draw(state: HeightFieldState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);
  const c = target.ctx;
  const bounds = ctx.field.bounds;

  const az = v.azimuth * DEG;
  const el = v.elevation * DEG;
  const cosA = Math.cos(az);
  const sinA = Math.sin(az);
  const cosE = Math.cos(el);
  const sinE = Math.sin(el);

  const extent = Math.max(1, Math.hypot(bounds.width, bounds.height) / 2);
  const camDistance = (extent / Math.tan((v.fov * DEG) / 2)) * v.distance;
  const focal = target.height / (2 * Math.tan((v.fov * DEG) / 2));
  const cx = target.width / 2;
  const cy = target.height / 2;
  const zRange = Math.max(1, state.maxZ - state.minZ);

  // Project once; every style reads from the same arrays.
  const size = state.cols * state.rows;
  const sx = new Float32Array(size);
  const sy = new Float32Array(size);
  const viewZ = new Float32Array(size);

  for (let i = 0; i < size; i++) {
    if (state.index[i] < 0) continue;
    const x = state.x[i];
    const y = state.y[i];
    const z = state.z[i];
    const rx = x * cosA - z * sinA;
    const rz0 = x * sinA + z * cosA;
    const ry = y * cosE - rz0 * sinE;
    const rz = y * sinE + rz0 * cosE;
    const vz = rz + camDistance;
    viewZ[i] = vz;
    if (vz < 1) continue;
    const scale = focal / vz;
    sx[i] = cx + rx * scale;
    sy[i] = cy + ry * scale;
  }

  const colorFor = (i: number): string => {
    let t: number;
    if (v.colorMode === 'height') t = clamp01((state.z[i] - state.minZ) / zRange);
    else if (v.colorMode === 'depth') t = clamp01(1 - (viewZ[i] - camDistance + extent) / (extent * 2));
    else t = 0;
    return v.colorMode === 'solid' ? state.palette.stop(0, v.opacity) : state.palette.at(t, v.opacity);
  };

  if (v.style === 'points') {
    const buckets = new Map<string, Path2D>();
    for (let i = 0; i < size; i++) {
      if (state.index[i] < 0 || viewZ[i] < 1) continue;
      const color = colorFor(i);
      let path = buckets.get(color);
      if (!path) {
        path = new Path2D();
        buckets.set(color, path);
      }
      const r = Math.max(0.4, v.lineWidth * 1.4);
      path.rect(sx[i] - r, sy[i] - r, r * 2, r * 2);
    }
    for (const [color, path] of buckets) {
      c.fillStyle = color;
      c.fill(path);
    }
    return;
  }

  if (v.style === 'solid') {
    // Painter's algorithm over quads, back to front by mean view depth.
    const quads: { i: number; depth: number }[] = [];
    for (let r = 0; r < state.rows - 1; r++) {
      for (let col = 0; col < state.cols - 1; col++) {
        const a = r * state.cols + col;
        const b = a + 1;
        const d = a + state.cols;
        const e = d + 1;
        if (state.index[a] < 0 || state.index[b] < 0 || state.index[d] < 0 || state.index[e] < 0) continue;
        quads.push({ i: a, depth: (viewZ[a] + viewZ[b] + viewZ[d] + viewZ[e]) / 4 });
      }
    }
    quads.sort((p, q) => q.depth - p.depth);

    for (const quad of quads) {
      const a = quad.i;
      const b = a + 1;
      const d = a + state.cols;
      const e = d + 1;
      c.fillStyle = colorFor(a);
      c.beginPath();
      c.moveTo(sx[a], sy[a]);
      c.lineTo(sx[b], sy[b]);
      c.lineTo(sx[e], sy[e]);
      c.lineTo(sx[d], sy[d]);
      c.closePath();
      c.fill();
    }
    return;
  }

  // Wireframe and contour lines: contours draw only the horizontal rows, which reads
  // as a topographic section rather than a mesh.
  c.lineWidth = v.lineWidth;
  const buckets = new Map<string, Path2D>();
  const link = (a: number, b: number): void => {
    if (state.index[a] < 0 || state.index[b] < 0) return;
    if (viewZ[a] < 1 || viewZ[b] < 1) return;
    const color = colorFor(a);
    let path = buckets.get(color);
    if (!path) {
      path = new Path2D();
      buckets.set(color, path);
    }
    path.moveTo(sx[a], sy[a]);
    path.lineTo(sx[b], sy[b]);
  };

  for (let r = 0; r < state.rows; r++) {
    for (let col = 0; col < state.cols; col++) {
      const i = r * state.cols + col;
      if (col + 1 < state.cols) link(i, i + 1);
      if (v.style === 'wireframe' && r + 1 < state.rows) link(i, i + state.cols);
    }
  }

  for (const [color, path] of buckets) {
    c.strokeStyle = color;
    c.stroke(path);
  }
}

export function elementCount(state: HeightFieldState): number {
  let n = 0;
  for (let i = 0; i < state.index.length; i++) if (state.index[i] >= 0) n++;
  return n;
}
